import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { Document, Page } from 'react-pdf';
import '@/helpers/preview/setup-pdfjs-worker';
import { useCachedBlobUrl } from '@/hooks/use-cached-blob-url';
import { useFlowControlSimulationCueStore } from '@/state/stores/use-flow-control-simulation-cue-store';
import { useLiveSequenceIndexStore } from '@/state/stores/use-live-sequence-index-store';
import { useLocalMediaPreviewSlideStore } from '@/state/stores/use-local-media-preview-slide-store';
import { useLiveSlideControlContext } from '@/components/preview/live-slide-control.context';
import { useControlSlideMutation } from '@/state/mutations/player/use-control-slide';
import { PresentationSlideJumpModal } from '@/components/preview/presentation-slide-jump-modal';
import { clampPresentationPage } from '@/components/preview/preview-presentation-slides-layer.helpers';
import type { PresentationSlideNavigationScope } from '@/components/preview/preview-presentation-slides-layer.types';
import { resolveLiveSlidePage, shouldSyncLiveSlideIndexFromLayer } from '@/helpers/resolve-live-slide-page.helper';
import { seedLocalMediaPreviewFromDurableResume } from '@/helpers/resolve-start-slide-index-for-take.helper';
export interface PreviewPdfLayerProps {
  url: string;
  /** Layer itemId, e.g. media:507f… */
  itemId?: string;
  slideNavigationScope?: PresentationSlideNavigationScope;
  /** Optional seed from layer.data.currentIndex when store has no value yet. */
  currentIndex?: number;
}

export function PreviewPdfLayer({
  url,
  itemId,
  slideNavigationScope = 'simulation',
  currentIndex: layerCurrentIndex,
}: PreviewPdfLayerProps) {
  const { url: blobUrl, loading: blobLoading, error: blobError } = useCachedBlobUrl(url, itemId);
  const mediaId = itemId?.startsWith('media:') ? itemId.slice('media:'.length) : itemId;
  const useSimulationNav = slideNavigationScope === 'simulation' && Boolean(itemId);
  const useLiveNav = slideNavigationScope === 'live' && Boolean(itemId);
  const useLocalNav = slideNavigationScope === 'local' && Boolean(mediaId);

  const simulationPage = useFlowControlSimulationCueStore((s) => (itemId ? (s.slideIndexByItemId[itemId] ?? 1) : 1));
  const reportSimulationPageCount = useFlowControlSimulationCueStore((s) => s.reportSlidePageCount);
  const stepSimulationSlide = useFlowControlSimulationCueStore((s) => s.stepSlide);
  const setSimulationSlideIndex = useFlowControlSimulationCueStore((s) => s.setSlideIndex);
  const simulationTotalPages = useFlowControlSimulationCueStore((s) =>
    itemId ? s.totalPagesByItemId[itemId] : undefined,
  );

  const liveIndexFromStore = useLiveSequenceIndexStore((s) => (itemId ? s.indexByItemId[itemId] : undefined));
  const setLiveSequenceIndex = useLiveSequenceIndexStore((s) => s.setIndex);
  const liveIndex = resolveLiveSlidePage({
    storeIndex: liveIndexFromStore,
    layerCurrentIndex,
  });
  const liveTotalPages = useLiveSequenceIndexStore((s) => (itemId ? s.totalPagesByItemId[itemId] : undefined));
  const reportLiveTotalPages = useLiveSequenceIndexStore((s) => s.reportTotalPages);

  const localPreviewPage = useLocalMediaPreviewSlideStore((s) => (mediaId ? (s.indexByMediaId[mediaId] ?? 1) : 1));
  const localPreviewTotalPages = useLocalMediaPreviewSlideStore((s) =>
    mediaId ? s.totalPagesByMediaId[mediaId] : undefined,
  );
  const stepLocalPreviewSlide = useLocalMediaPreviewSlideStore((s) => s.stepSlide);
  const setLocalPreviewSlideIndex = useLocalMediaPreviewSlideStore((s) => s.setSlideIndex);
  const reportLocalPreviewTotalPages = useLocalMediaPreviewSlideStore((s) => s.reportTotalPages);

  const liveSlideControl = useLiveSlideControlContext();
  const { mutate: controlSlide } = useControlSlideMutation();
  const canLiveSlideControl = useLiveNav && Boolean(liveSlideControl) && Boolean(mediaId);

  useEffect(() => {
    if (!useLocalNav || !itemId) {
      return;
    }
    seedLocalMediaPreviewFromDurableResume(itemId);
  }, [useLocalNav, itemId]);

  const pageNumber = useLiveNav ? liveIndex : useSimulationNav ? simulationPage : useLocalNav ? localPreviewPage : 1;
  const totalPages = useLiveNav
    ? liveTotalPages
    : useSimulationNav
      ? simulationTotalPages
      : useLocalNav
        ? localPreviewTotalPages
        : undefined;

  const [docLoading, setDocLoading] = useState(true);
  const [docError, setDocError] = useState<string | null>(null);
  const [containerHeight, setContainerHeight] = useState(600);
  const [jumpModalOpen, setJumpModalOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const onDocumentLoadSuccess = useCallback(
    ({ numPages }: { numPages: number }) => {
      setDocLoading(false);
      setDocError(null);
      if (!itemId || numPages <= 0) {
        return;
      }
      if (useLiveNav) {
        reportLiveTotalPages(itemId, numPages);
        return;
      }
      if (useSimulationNav) {
        reportSimulationPageCount(itemId, numPages);
        return;
      }
      if (useLocalNav && mediaId) {
        reportLocalPreviewTotalPages(mediaId, numPages);
      }
    },
    [
      itemId,
      mediaId,
      useLiveNav,
      useSimulationNav,
      useLocalNav,
      reportLiveTotalPages,
      reportSimulationPageCount,
      reportLocalPreviewTotalPages,
    ],
  );

  const onDocumentLoadError = useCallback((err: Error) => {
    console.error('Error loading PDF:', err);
    setDocError('Failed to load PDF document');
    setDocLoading(false);
  }, []);

  useEffect(() => {
    setDocLoading(true);
    setDocError(null);
  }, [blobUrl]);

  useEffect(() => {
    if (!itemId) {
      return;
    }
    const explicitLayerIndex =
      layerCurrentIndex != null && Number.isFinite(layerCurrentIndex) && layerCurrentIndex > 0
        ? layerCurrentIndex
        : undefined;

    if (useLiveNav) {
      const liveStore = useLiveSequenceIndexStore.getState();
      const syncPage = shouldSyncLiveSlideIndexFromLayer({
        storeIndex: liveStore.indexByItemId[itemId],
        layerCurrentIndex: explicitLayerIndex,
        recentlyOptimistic: liveStore.isRecentlyOptimistic(itemId),
      });
      if (syncPage != null) {
        setLiveSequenceIndex(itemId, syncPage);
      }
      return;
    }

    if (useSimulationNav) {
      const existing = useFlowControlSimulationCueStore.getState().slideIndexByItemId[itemId];
      if (existing == null && explicitLayerIndex != null) {
        setSimulationSlideIndex(itemId, explicitLayerIndex);
      }
      return;
    }

    if (useLocalNav && mediaId) {
      const existing = useLocalMediaPreviewSlideStore.getState().indexByMediaId[mediaId];
      if (existing == null && explicitLayerIndex != null) {
        setLocalPreviewSlideIndex(mediaId, explicitLayerIndex);
      }
    }
  }, [
    itemId,
    mediaId,
    layerCurrentIndex,
    useLiveNav,
    useSimulationNav,
    useLocalNav,
    setLiveSequenceIndex,
    setSimulationSlideIndex,
    setLocalPreviewSlideIndex,
  ]);

  useEffect(() => {
    const updateHeight = () => {
      const height = containerRef.current?.clientHeight ?? 600;
      setContainerHeight(height);
    };
    const timer = setTimeout(updateHeight, 100);
    const ro = new ResizeObserver(updateHeight);
    if (containerRef.current) {
      ro.observe(containerRef.current);
    }
    return () => {
      clearTimeout(timer);
      ro.disconnect();
    };
  }, []);

  const goToPage = useCallback(
    (page: number) => {
      if (!itemId || !totalPages) {
        return;
      }
      const target = clampPresentationPage(page, totalPages);
      if (canLiveSlideControl && liveSlideControl && mediaId) {
        const previous = liveIndex;
        if (target !== previous) {
          setLiveSequenceIndex(itemId, target, { optimistic: true });
        }
        // Fire-and-forget: never block UI / overlapping gotos on control-slide RTT.
        // Do not invalidate sequence-status on success — that GET poll re-stomps the optimistic badge.
        controlSlide(
          {
            mediaId,
            entityType: liveSlideControl.entityType,
            entityId: liveSlideControl.entityId,
            action: 'goto',
            slideNumber: target,
          },
          {
            onError: (error) => {
              // Keep optimistic page so Next look-ahead selection survives until take.
              console.error('[PdfSlideControl] Error:', error);
            },
          },
        );
        return;
      }
      if (useLiveNav) {
        return;
      }
      if (useSimulationNav) {
        setSimulationSlideIndex(itemId, target);
        return;
      }
      if (useLocalNav && mediaId) {
        setLocalPreviewSlideIndex(mediaId, target, { operatorPick: true });
      }
    },
    [
      itemId,
      totalPages,
      canLiveSlideControl,
      liveSlideControl,
      mediaId,
      liveIndex,
      setLiveSequenceIndex,
      controlSlide,
      useLiveNav,
      useSimulationNav,
      useLocalNav,
      setSimulationSlideIndex,
      setLocalPreviewSlideIndex,
    ],
  );

  const goPage = useCallback(
    (delta: number) => {
      if (!itemId || !totalPages) {
        return;
      }
      if (canLiveSlideControl && liveSlideControl && mediaId) {
        void goToPage(pageNumber + delta);
        return;
      }
      if (useLiveNav) {
        return;
      }
      if (useSimulationNav) {
        stepSimulationSlide(itemId, delta);
        return;
      }
      if (useLocalNav && mediaId) {
        stepLocalPreviewSlide(mediaId, delta);
      }
    },
    [
      itemId,
      totalPages,
      canLiveSlideControl,
      liveSlideControl,
      mediaId,
      goToPage,
      pageNumber,
      useLiveNav,
      useSimulationNav,
      useLocalNav,
      stepSimulationSlide,
      stepLocalPreviewSlide,
    ],
  );

  const error = blobError ? 'Failed to load PDF document' : docError;
  const loading = blobLoading || (blobUrl && docLoading);
  const effectivePage = totalPages ? Math.min(Math.max(1, pageNumber), totalPages) : Math.max(1, pageNumber);
  const showSlideNav = Boolean(itemId) && (!useLiveNav || canLiveSlideControl);
  const canGoPrev = showSlideNav && effectivePage > 1;
  const canGoNext = showSlideNav && !!totalPages && effectivePage < totalPages;

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          bgcolor: '#000',
        }}
      >
        <Typography variant="body2" color="error">
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      sx={{ width: '100%', height: '100%', position: 'relative', overflow: 'hidden', bgcolor: '#000' }}
    >
      {loading && (
        <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 10 }}>
          <Typography variant="body2" color="text.secondary">
            Loading PDF...
          </Typography>
        </Box>
      )}
      {blobUrl ? (
        <Document
          key={blobUrl}
          file={blobUrl}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={onDocumentLoadError}
          loading=""
          error=""
        >
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: '#000',
            }}
          >
            <Box sx={{ bgcolor: '#fff', lineHeight: 0 }}>
              <Page
                key={effectivePage}
                pageNumber={effectivePage}
                renderTextLayer={false}
                renderAnnotationLayer={false}
                height={containerHeight}
                scale={1}
              />
            </Box>
          </Box>
        </Document>
      ) : null}

      {canGoPrev && !jumpModalOpen ? (
        <IconButton aria-label="Previous page" onClick={() => goPage(-1)} size="large" sx={pdfNavButtonSx('left')}>
          <ChevronLeft fontSize="large" />
        </IconButton>
      ) : null}
      {canGoNext && !jumpModalOpen ? (
        <IconButton aria-label="Next page" onClick={() => goPage(1)} size="large" sx={pdfNavButtonSx('right')}>
          <ChevronRight fontSize="large" />
        </IconButton>
      ) : null}
      {totalPages && totalPages > 0 ? (
        <>
          <Box
            component={showSlideNav ? 'button' : 'div'}
            type={showSlideNav ? 'button' : undefined}
            onClick={showSlideNav ? () => setJumpModalOpen(true) : undefined}
            aria-label={`Page ${effectivePage} of ${totalPages}`}
            sx={{
              position: 'absolute',
              bottom: 16,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 2,
              m: 0,
              px: 1.5,
              py: 0.5,
              minHeight: 28,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              appearance: 'none',
              border: 'none',
              borderRadius: 1,
              cursor: showSlideNav ? 'pointer' : 'default',
              bgcolor: 'rgba(255,255,255,0.9)',
              color: 'rgba(0,0,0,0.75)',
              fontSize: '0.875rem',
              fontFamily: 'inherit',
              fontWeight: 500,
              lineHeight: 1.43,
              whiteSpace: 'nowrap',
              userSelect: 'none',
              boxSizing: 'border-box',
              ...(showSlideNav && {
                '&:hover': {
                  bgcolor: '#fff',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.18)',
                },
              }),
            }}
          >
            {effectivePage} / {totalPages}
          </Box>
          {showSlideNav ? (
            <PresentationSlideJumpModal
              open={jumpModalOpen}
              onClose={() => setJumpModalOpen(false)}
              currentPage={effectivePage}
              totalPages={totalPages}
              onJump={(page) => {
                void goToPage(page);
              }}
            />
          ) : null}
        </>
      ) : null}
    </Box>
  );
}

function pdfNavButtonSx(side: 'left' | 'right') {
  return {
    'position': 'absolute',
    'top': '50%',
    [side]: 8,
    'transform': 'translateY(-50%)',
    'zIndex': 2,
    'color': '#fff',
    'bgcolor': 'rgba(0,0,0,0.45)',
    '&:hover': { bgcolor: 'rgba(0,0,0,0.65)' },
  } as const;
}
